import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RenovarProjetoDialog } from "@/components/controle-projetos/RenovarProjetoDialog";
import { FinalizarProjetoDialog } from "@/components/controle-projetos/FinalizarProjetoDialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { writeExpectingRows } from "@/lib/supabase-write";
import { userErrorToast } from "@/lib/report-error";
import { trackEvent } from "@/lib/telemetry";
import { softDeleteControleProjeto } from "@/utils/controle-projeto-delete";
import { formatDateBR, formatLocalDate } from "@/utils/dateUtils";
import { calcularEstadoProjeto } from "@/utils/controle-projeto-estado";
import type { ControleProjeto } from "@/types/controle-projeto";

export interface ProjectActions {
  onRenovar: (projeto: ControleProjeto) => void;
  onFinalizar: (projeto: ControleProjeto) => void;
  onDesfazerFinalizacao: (projeto: ControleProjeto) => void;
  onConfirmarPlanejado: (projeto: ControleProjeto) => void;
  onExcluir: (projetoId: string) => void;
}

/**
 * Renovar, Finalizar, Desfazer finalização e Excluir de um projeto, com os dialogs de
 * cada um. Usado em /controle-projetos e no perfil do freelancer, para as duas telas
 * gravarem do mesmo jeito. Renderize `dialogs` como irmão de qualquer outro dialog.
 */
export function useProjectActions() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [projectToRenew, setProjectToRenew] = useState<ControleProjeto | null>(null);
  const [projectToFinish, setProjectToFinish] = useState<ControleProjeto | null>(null);
  const [projectToDelete, setProjectToDelete] = useState<string | null>(null);

  const refreshProjectLists = () => {
    queryClient.invalidateQueries({ queryKey: ["controle-projetos"] });
    queryClient.invalidateQueries({ queryKey: ["freelancer-projetos"] });
    queryClient.invalidateQueries({ queryKey: ["projetos-andamento-freelancer"] });
  };

  // Renovar = linha NOVA apontando pra raiz da cadeia. O projeto antigo vira 'renovado'
  // por trigger no banco (sql/187), então aqui só inserimos e invalidamos.
  const confirmRenewal = async (novaDataInicio: Date, novaDataConclusao: Date) => {
    const projetoOriginal = projectToRenew;
    if (!projetoOriginal) return;
    try {
      const numeroRenovacoes = (projetoOriginal.numero_renovacoes || 0) + 1;

      const novoProjeto = {
        agencia_id: projetoOriginal.agencia_id,
        freelancer_id: projetoOriginal.freelancer_id,
        area_atuacao: projetoOriginal.area_atuacao,
        nucleo: projetoOriginal.nucleo,
        valor_projeto: projetoOriginal.valor_projeto,
        data_inicio: formatLocalDate(novaDataInicio),
        data_conclusao: formatLocalDate(novaDataConclusao),
        numero_renovacoes: numeroRenovacoes,
        projeto_original_id: projetoOriginal.projeto_original_id || projetoOriginal.id,
      };

      await writeExpectingRows(
        supabase.from("controle_projetos").insert([novoProjeto]),
        "renovar projeto",
      );
      trackEvent("projeto.renovar", { entidade: { tipo: "projeto", id: projetoOriginal.id } });

      toast({
        title: "Sucesso",
        description: `Projeto renovado com sucesso! (${numeroRenovacoes}ª renovação)`,
      });

      refreshProjectLists();
    } catch (error) {
      toast(userErrorToast(error, { action: "renovar o projeto", source: "controle-projetos" }));
    }
  };

  const confirmFinish = async (projeto: ControleProjeto, novaDataConclusao?: Date) => {
    setProjectToFinish(null);
    const dataConclusao = novaDataConclusao ? formatLocalDate(novaDataConclusao) : projeto.data_conclusao;
    try {
      await writeExpectingRows(
        supabase
          .from("controle_projetos")
          .update(novaDataConclusao ? { status: "encerrado", data_conclusao: dataConclusao } : { status: "encerrado" })
          .eq("id", projeto.id),
        "finalizar projeto",
      );
      await cancelPlanned(projeto);
      trackEvent("projeto.encerrar", { entidade: { tipo: "projeto", id: projeto.id } });
      const aindaEmCurso = calcularEstadoProjeto({ ...projeto, status: "encerrado", data_conclusao: dataConclusao ?? null }).estado === "nao_renova";
      toast({
        title: "Projeto finalizado",
        description: aindaEmCurso
          ? `Segue em andamento até ${formatDateBR(dataConclusao)} e vai para o histórico depois.`
          : "Para reabrir, edite a data de conclusão para uma data futura.",
      });
      refreshProjectLists();
    } catch (error) {
      toast(userErrorToast(error, { action: "finalizar o projeto", source: "controle-projetos" }));
    }
  };

  // Quem não renova não cumpre os períodos já combinados: saem por soft delete.
  const cancelPlanned = async (projeto: ControleProjeto) => {
    const ids = (projeto.planejados ?? []).map((planejado) => planejado.id);
    if (ids.length === 0) return;
    await writeExpectingRows(
      supabase
        .from("controle_projetos")
        .update({ deleted_at: new Date().toISOString() })
        .in("id", ids)
        .eq("status", "planejado"),
      "cancelar períodos planejados",
    );
  };

  // O próximo planejado vira 'ativo'; o trigger da sql/282 marca o período atual 'renovado'.
  const confirmPlanned = async (projeto: ControleProjeto) => {
    const proximo = projeto.planejados?.[0];
    if (!proximo) return;
    try {
      await writeExpectingRows(
        supabase
          .from("controle_projetos")
          .update({ status: "ativo" })
          .eq("id", proximo.id)
          .eq("status", "planejado"),
        "confirmar período planejado",
      );
      trackEvent("projeto.confirmar_planejado", { entidade: { tipo: "projeto", id: projeto.id } });
      toast({
        title: "Próximo período confirmado",
        description: proximo.data_conclusao
          ? `Segue até ${formatDateBR(proximo.data_conclusao)}.`
          : "O período planejado está em curso.",
      });
      refreshProjectLists();
    } catch (error) {
      toast(userErrorToast(error, { action: "confirmar o próximo período", source: "controle-projetos" }));
    }
  };

  const undoFinish = async (projeto: ControleProjeto) => {
    try {
      await writeExpectingRows(
        supabase.from("controle_projetos").update({ status: "ativo" }).eq("id", projeto.id),
        "desfazer finalização",
      );
      toast({ title: "Finalização desfeita", description: "O projeto voltou a ficar ativo." });
      refreshProjectLists();
    } catch (error) {
      toast(userErrorToast(error, { action: "desfazer a finalização", source: "controle-projetos" }));
    }
  };

  const confirmDelete = async (projetoId: string) => {
    try {
      await softDeleteControleProjeto(projetoId);

      toast({
        title: "Sucesso",
        description: "Projeto excluído com sucesso!",
      });

      refreshProjectLists();
      queryClient.invalidateQueries({ queryKey: ["job-evaluations"] });
    } catch (error) {
      toast(userErrorToast(error, { action: "excluir o projeto", source: "controle-projetos" }));
    }
  };

  const actions: ProjectActions = {
    onRenovar: setProjectToRenew,
    onFinalizar: setProjectToFinish,
    onDesfazerFinalizacao: undoFinish,
    onConfirmarPlanejado: (projeto) => void confirmPlanned(projeto),
    onExcluir: setProjectToDelete,
  };

  const dialogs = (
    <>
      <RenovarProjetoDialog
        isOpen={!!projectToRenew}
        onClose={() => setProjectToRenew(null)}
        projeto={projectToRenew}
        onConfirm={confirmRenewal}
      />

      <FinalizarProjetoDialog
        key={projectToFinish?.id ?? "fechado"}
        projeto={projectToFinish}
        onClose={() => setProjectToFinish(null)}
        onConfirm={confirmFinish}
      />

      <AlertDialog open={!!projectToDelete} onOpenChange={(open) => !open && setProjectToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir este projeto?</AlertDialogTitle>
            <AlertDialogDescription>
              O projeto sai do controle e as avaliações dele que ainda não foram respondidas deixam de
              ser enviadas. Avaliações já respondidas continuam guardadas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Manter projeto</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (projectToDelete) void confirmDelete(projectToDelete);
                setProjectToDelete(null);
              }}
            >
              Excluir projeto
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  return { actions, dialogs };
}
